import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Search, Plus, Trash2, RefreshCcw, ShoppingBag, AlertTriangle, AlertCircle, CheckCircle, Calendar, ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter, Eye, X, XCircle, LayoutGrid, Shield, Layers, Gauge, FileText, Clock, List, LayoutList, Check, PauseCircle, Package, DollarSign, Truck, Receipt, CreditCard } from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import AchatsFormModalTabs from './components/AchatsFormModalTabs';
import { buildAchatsColumnDefs, achatsColHeaderProps, ACHATS_FORM_MODAL_SHELL, ACHATS_FORM_TAB_PANEL, ACHATS_FORM_GRID, useAchatsModalTheme, useAchatsColumnVisibility, achatsTableColSpan, ACHATS_TABLE_ACTIONS_TH_CLASS, useAchatsRowSelection, buildAchatsBulkDeleteFilterAction, AchatsBulkDeleteBar, useAppNotificationToast, AchatsCrudNotificationToast, sortRecordsNewestFirst, notifyCreated, notifyUpdated, notifyDeleted, notifyBulkDeleted, notifyError, useAchatsListViewMode, useAchatsListDisplay, fetchAchatsSuppliers } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { buildExportFilename } from '../../shared/utils/exportFilename';
import { generateReportPDF } from '../../shared/utils/reportPdfGenerator';
import { generateReportExcel } from '../../shared/utils/reportExcelGeneratorAdvanced';
import { CustomSelect, CustomDatePicker, CustomNumberInput } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { makePageT } from '../../shared/utils/pageI18n';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

const LivreDesAchatsPage = () => {
  const { t, i18n } = useTranslation();
  const p = makePageT(t, 'modules.achats.pages.livreDesAchatsPage');
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const dateLocale = getBrandLocale(i18n.language);
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString(dateLocale) : '-';

  const getOrderStatusLabel = (status) => {
    const map = {
      pending: p('order_pending'),
      confirmed: p('order_confirmed'),
      in_transit: p('order_in_transit'),
      received: p('order_received'),
      cancelled: p('order_cancelled'),
    };
    return map[status] || status;
  };

  const getPaymentMethodLabel = (method) => {
    const map = {
      cash: p('method_cash'),
      check: p('method_check'),
      transfer: p('method_transfer'),
      mada: p('method_mada'),
      card: p('method_card'),
      credit: p('method_credit'),
    };
    return map[method] || method;
  };

  const statusOptions = [
    { value: 'pending', label: p('pay_pending'), icon: Clock, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'partial', label: p('pay_partial'), icon: AlertCircle, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'paid', label: p('pay_paid'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'cancelled', label: p('pay_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ];

  const paymentMethodOptions = [
    { value: 'cash', label: p('method_cash') },
    { value: 'check', label: p('method_check') },
    { value: 'transfer', label: p('method_transfer') },
    { value: 'mada', label: p('method_mada') },
    { value: 'card', label: p('method_card') },
    { value: 'credit', label: p('method_credit') },
  ];

  // Close dropdowns on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const isClickOnStatusButton = event.target.closest('[data-purchase-status-dropdown-button]');
      const isClickInStatusDropdown = event.target.closest('[data-purchase-status-dropdown]');
      if (!isClickOnStatusButton && !isClickInStatusDropdown) {
        setShowStatusDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [purchases, setPurchases] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  
  // Collapsible sections state
  const [formTab, setFormTab] = useState('general');
  const formTabs = useMemo(() => ([
    { id: 'general', label: p('section_identification'), icon: FileText, iconColor: 'text-blue-400' },
    { id: 'financial', label: p('section_amounts'), icon: DollarSign, iconColor: 'text-green-400' },
  ]), [p]);
  const [formData, setFormData] = useState({ 
    supplier_id: '', 
    supplier_name: '',
    supplier_code: '',
    purchase_date: new Date().toISOString().split('T')[0],
    invoice_number: '',
    invoice_date: null,
    due_date: null,
    total_amount: '',
    paid_amount: '',
    remaining_amount: '',
    payment_status: 'pending',
    payment_method: '',
    warehouse_id: '',
    warehouse_name: '',
    product_count: '',
    tax_amount: '',
    discount_amount: '',
    notes: ''
  });
  
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_purchases');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  
  // PDF and Excel export states
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const [viewType, setViewType] = useState('manual'); // 'manual' or 'converted'
  
  // Orders for conversion state (Depuis Commandes)
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [convertingOrderId, setConvertingOrderId] = useState(null);
  
  // Dropdown data states
  const [availableSuppliers, setAvailableSuppliers] = useState([]);
  const [loadingSuppliers, setLoadingSuppliers] = useState(false);
  const [availableWarehouses, setAvailableWarehouses] = useState([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  
  const dateRangeRef = useRef(null);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const statusDropdownRef = useRef(null);
  const statusFilterRef = useRef(null);
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();

  useEffect(() => { 
    loadPurchases(); 
    loadSuppliers();
    loadWarehouses();
  }, []);
  
  useEffect(() => {
    if (viewType === 'manual') {
      loadPurchases();
    } else {
      loadOrders();
    }
    // Reset search when switching tabs
    setSearchTerm('');
    setSelectedRecords([]);
  }, [viewType]);
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) setShowStatusDropdown(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadPurchases = async () => {
    try {
      const response = await localApiAdapter.get(`/achats/livre-des-achats?source_type=${viewType}`);
      setPurchases(sortRecordsNewestFirst(response?.data || response || []));
    } catch (error) {
      console.error('Error loading purchases:', error);
      setPurchases([]);
    }
  };

  const loadOrders = async () => {
    try {
      setLoadingOrders(true);
      const response = await localApiAdapter.get('/achats/livre-des-achats/orders-for-conversion');
      setOrders(response?.data || response || []);
    } catch (error) {
      console.error('Error loading orders for conversion:', error);
      setOrders([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleConvertOrder = async (orderId) => {
    if (!window.confirm(p('convert_confirm'))) return;
    try {
      setConvertingOrderId(orderId);
      await localApiAdapter.post(`/achats/livre-des-achats/convert/${orderId}`);
      await loadOrders(); // Refresh to show updated conversion status
    } catch (error) {
      console.error('Error converting order:', error);
      const errorMsg = error?.response?.data?.error || error?.message || p('convert_error');
      alert(errorMsg);
    } finally {
      setConvertingOrderId(null);
    }
  };

  const getOrderStatusBadge = (status) => {
    const statusConfig = {
      pending: { bg: 'bg-orange-500/20', text: 'text-orange-300', border: 'border-orange-500/30' },
      confirmed: { bg: 'bg-blue-500/20', text: 'text-blue-300', border: 'border-blue-500/30' },
      in_transit: { bg: 'bg-purple-500/20', text: 'text-purple-300', border: 'border-purple-500/30' },
      received: { bg: 'bg-green-500/20', text: 'text-green-300', border: 'border-green-500/30' },
      cancelled: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/30' },
    };
    const config = statusConfig[status] || statusConfig.pending;
    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${config.bg} ${config.text} ${config.border}`}>
        {getOrderStatusLabel(status)}
      </span>
    );
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = !searchTerm || 
      (order.order_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (order.supplier_name || '').toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const loadSuppliers = async () => {
    try {
      setLoadingSuppliers(true);
      setAvailableSuppliers(await fetchAchatsSuppliers());
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setAvailableSuppliers([]);
    } finally {
      setLoadingSuppliers(false);
    }
  };
  
  const loadWarehouses = async () => {
    try {
      setLoadingWarehouses(true);
      const response = await localApiAdapter.get('/inventory/warehouses');
      setAvailableWarehouses(response?.data || response || []);
    } catch (error) {
      console.error('Error loading warehouses:', error);
      setAvailableWarehouses([]);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const handleSupplierSelection = (supplierId) => {
    if (!supplierId) {
      setFormData(prev => ({ ...prev, supplier_id: '', supplier_name: '', supplier_code: '' }));
      return;
    }
    const supplier = availableSuppliers.find(s => s.id.toString() === supplierId.toString());
    if (supplier) {
      setFormData(prev => ({ 
        ...prev, 
        supplier_id: supplierId,
        supplier_name: supplier.company_name || supplier.name || '',
        supplier_code: supplier.code || ''
      }));
    }
  };

  const stats = {
    total: purchases.length,
    pending: purchases.filter(p => p.payment_status === 'pending').length,
    partial: purchases.filter(p => p.payment_status === 'partial').length,
    paid: purchases.filter(p => p.payment_status === 'paid').length,
    overdue: purchases.filter(p => p.due_date && new Date(p.due_date) < new Date() && p.payment_status !== 'paid').length,
    totalAmount: purchases.reduce((sum, p) => sum + (parseFloat(p.total_amount) || 0), 0),
    totalRemaining: purchases.reduce((sum, p) => sum + (parseFloat(p.remaining_amount) || 0), 0)
  };

  const mainTabs = [
    { id: 'all_purchases', name: p('tab_general'), icon: LayoutGrid },
    { id: 'par_paiement', name: p('tab_by_payment'), icon: CreditCard },
    { id: 'par_fournisseur', name: p('tab_by_supplier'), icon: Truck },
  ];

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: Layers },
    { id: 'pending', name: p('filter_pending'), icon: Clock },
    { id: 'partial', name: p('filter_partial'), icon: AlertCircle },
    { id: 'paid', name: p('filter_paid'), icon: CheckCircle },
  ];

  const purchaseDistribution = {
    all_purchases: {
      title: p('dist_overview_title'),
      data: [
        { filterKey: 'pending', name: p('dist_pending'), count: stats.pending, color: 'bg-gradient-to-r from-orange-500 to-orange-600' },
        { filterKey: 'partial', name: p('dist_partial'), count: stats.partial, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'paid', name: p('dist_paid'), count: stats.paid, color: 'bg-gradient-to-r from-green-500 to-green-600' },
        { filterKey: 'overdue', name: p('dist_overdue'), count: stats.overdue, color: 'bg-gradient-to-r from-red-500 to-red-600' },
      ],
    },
    par_paiement: {
      title: p('dist_by_payment_title'),
      data: [
        { filterKey: 'unpaid', name: p('dist_unpaid'), count: stats.pending, color: 'bg-gradient-to-r from-red-500 to-red-600' },
        { filterKey: 'partial', name: p('dist_partials'), count: stats.partial, color: 'bg-gradient-to-r from-yellow-500 to-yellow-600' },
        { filterKey: 'complete', name: p('dist_complete'), count: stats.paid, color: 'bg-gradient-to-r from-green-500 to-green-600' },
      ],
    },
    par_fournisseur: {
      title: p('dist_by_supplier_title'),
      data: (() => {
        const supplierCounts = {};
        purchases.forEach((rec) => {
          const supplier = rec.supplier_name || p('supplier_unspecified');
          supplierCounts[supplier] = (supplierCounts[supplier] || 0) + 1;
        });
        const topSuppliers = Object.entries(supplierCounts)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 4);
        const colors = [
          'bg-gradient-to-r from-blue-500 to-blue-600',
          'bg-gradient-to-r from-purple-500 to-purple-600',
          'bg-gradient-to-r from-pink-500 to-pink-600',
          'bg-gradient-to-r from-indigo-500 to-indigo-600',
        ];
        return topSuppliers.map(([name, count], i) => ({
          filterKey: name,
          name,
          count,
          color: colors[i],
        }));
      })(),
    },
  };

  const distributionInfo = purchaseDistribution[mainViewMode] || purchaseDistribution.all_purchases;

  const baseFilteredPurchases = purchases.filter(p => {
    if (dateRange.start || dateRange.end) {
      const purchaseDate = new Date(p.purchase_date || p.created_at);
      
      if (dateRange.start) {
        const startOfDay = new Date(dateRange.start);
        startOfDay.setHours(0, 0, 0, 0);
        if (purchaseDate < startOfDay) return false;
      }
      
      if (dateRange.end) {
        const endOfDay = new Date(dateRange.end);
        endOfDay.setHours(23, 59, 59, 999);
        if (purchaseDate > endOfDay) return false;
      }
    }
    
    if (statusFilter !== 'all' && p.payment_status !== statusFilter) return false;
    
    if (searchTerm && !p.purchase_number?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !p.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        !p.invoice_number?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    
    if (mainViewMode === 'all_purchases') {
      if (quickFilter === 'pending' && p.payment_status !== 'pending') return false;
      if (quickFilter === 'partial' && p.payment_status !== 'partial') return false;
      if (quickFilter === 'paid' && p.payment_status !== 'paid') return false;
    }
    
    if (activeDistributionFilter) {
      switch (mainViewMode) {
        case 'all_purchases':
          if (activeDistributionFilter === 'pending' && p.payment_status !== 'pending') return false;
          if (activeDistributionFilter === 'partial' && p.payment_status !== 'partial') return false;
          if (activeDistributionFilter === 'paid' && p.payment_status !== 'paid') return false;
          if (activeDistributionFilter === 'overdue' && (p.payment_status === 'paid' || !p.due_date || new Date(p.due_date) >= new Date())) return false;
          break;
        case 'par_paiement':
          if (activeDistributionFilter === 'unpaid' && p.payment_status !== 'pending') return false;
          if (activeDistributionFilter === 'partial' && p.payment_status !== 'partial') return false;
          if (activeDistributionFilter === 'complete' && p.payment_status !== 'paid') return false;
          break;
        case 'par_fournisseur':
          if (activeDistributionFilter && p.supplier_name !== activeDistributionFilter) return false;
          break;
        default:
          break;
      }
    }
    
    return true;
  });

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('purchase_number'),
      textColumnRule('supplier_name'),
      textColumnRule('purchase_date', (r) => (r.purchase_date ? String(r.purchase_date).split('T')[0] : '')),
      textColumnRule('total_amount', (r) => String(r.total_amount ?? '')),
      textColumnRule('payment_method'),
      exactColumnRule('payment_status', (r) => r.payment_status || ''),
    ],
    [],
  );

  const { filtered: filteredPurchases, displayed: displayedPurchases } = useAchatsListDisplay(
    baseFilteredPurchases,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedRecords, filteredPurchases);


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredPurchases.map((r) => r.id));
    }
  };

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { 
        supplier_id: parseInt(formData.supplier_id) || null,
        supplier_name: formData.supplier_name,
        supplier_code: formData.supplier_code,
        purchase_date: formData.purchase_date ? new Date(formData.purchase_date).toISOString() : null,
        invoice_number: formData.invoice_number,
        invoice_date: formData.invoice_date ? new Date(formData.invoice_date).toISOString() : null,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        total_amount: parseFloat(formData.total_amount) || 0,
        paid_amount: parseFloat(formData.paid_amount) || 0,
        remaining_amount: parseFloat(formData.remaining_amount) || 0,
        payment_status: formData.payment_status,
        payment_method: formData.payment_method,
        warehouse_id: parseInt(formData.warehouse_id) || null,
        warehouse_name: formData.warehouse_name,
        product_count: parseInt(formData.product_count) || 0,
        tax_amount: parseFloat(formData.tax_amount) || 0,
        discount_amount: parseFloat(formData.discount_amount) || 0,
        notes: formData.notes || ''
      };
      
      if (editingRecord) {
        await localApiAdapter.put(`/achats/livre-des-achats/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/achats/livre-des-achats', submitData);
        notifyCreated(showNotify, entityLabel);
      }
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('alert_save_error', { message: error.message || p('alert_unknown_error') }));
    } finally {
      resetForm();
      loadPurchases();
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({ 
      purchase_number: record.purchase_number || '',
      supplier_id: record.supplier_id?.toString() || '',
      supplier_name: record.supplier_name || '',
      supplier_code: record.supplier_code || '',
      purchase_date: record.purchase_date ? new Date(record.purchase_date) : null,
      invoice_number: record.invoice_number || '',
      invoice_date: record.invoice_date ? new Date(record.invoice_date) : null,
      due_date: record.due_date ? new Date(record.due_date) : null,
      total_amount: record.total_amount?.toString() || '',
      paid_amount: record.paid_amount?.toString() || '',
      remaining_amount: record.remaining_amount?.toString() || '',
      payment_status: record.payment_status || 'pending',
      payment_method: record.payment_method || '',
      warehouse_id: record.warehouse_id?.toString() || '',
      warehouse_name: record.warehouse_name || '',
      product_count: record.product_count?.toString() || '',
      tax_amount: record.tax_amount?.toString() || '',
      discount_amount: record.discount_amount?.toString() || '',
      notes: record.notes || ''
    });
    setFormTab('general');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        await localApiAdapter.delete(`/achats/livre-des-achats/${id}`);
        notifyDeleted(showNotify, entityLabel);
        loadPurchases();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('alert_save_error', { message: p('alert_unknown_error') }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('delete_bulk_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map(id => localApiAdapter.delete(`/achats/livre-des-achats/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedRecords([]);
        loadPurchases();
      } catch (error) {
        console.error('Error deleting records:', error);
        notifyError(showNotify, p('alert_save_error', { message: p('alert_unknown_error') }));
      }
    }
  };

  const resetForm = () => {
    setFormData({ 
      supplier_id: '', 
      supplier_name: '',
      supplier_code: '',
      purchase_date: new Date().toISOString().split('T')[0],
      invoice_number: '',
      invoice_date: null,
      due_date: null,
      total_amount: '',
      paid_amount: '',
      remaining_amount: '',
      payment_status: 'pending',
      payment_method: '',
      warehouse_id: '',
      warehouse_name: '',
      product_count: '',
      tax_amount: '',
      discount_amount: '',
      notes: ''
    });
    setEditingRecord(null);
    setFormTab('general');
    setShowModal(false);
  };

  // Export PDF
  const handleExportPDF = async () => {
    try {
      setIsGeneratingPDF(true);
      
      const filteredData = filteredPurchases;
      
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = `
        <div class="report-content">
          <table>
            <thead>
              <tr>
                <th>${p('col_purchase_number')}</th>
                <th>${p('col_supplier')}</th>
                <th>${p('col_date')}</th>
                <th>${p('export_total')}</th>
                <th>${p('export_paid')}</th>
                <th>${p('export_remaining')}</th>
                <th>${p('col_status')}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map(item => `
                <tr>
                  <td>${item.purchase_number || p('na')}</td>
                  <td>${item.supplier_name || p('na')}</td>
                  <td>${item.purchase_date ? fmtDate(item.purchase_date) : p('na')}</td>
                  <td>${formatBrandMoney(item.total_amount || 0)}</td>
                  <td>${formatBrandMoney(item.paid_amount || 0)}</td>
                  <td>${formatBrandMoney(item.remaining_amount || 0)}</td>
                  <td>${item.payment_status || p('na')}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
      
      document.body.appendChild(tempDiv);
      await generateReportPDF(p('report_title'), {});
      document.body.removeChild(tempDiv);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert(p('alert_pdf_error'));
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  // Export Excel (Advanced)
  const handleExportExcel = async () => {
    try {
      setIsGeneratingExcel(true);
      
      const filteredData = filteredPurchases;
      
      const columns = [
        { key: 'purchase_number', label: p('export_purchase_number'), width: 20 },
        { key: 'supplier_name', label: p('export_supplier'), width: 25 },
        { key: 'invoice_number', label: p('export_invoice_number'), width: 20 },
        { key: 'purchase_date', label: p('export_purchase_date'), width: 15 },
        { key: 'total_amount', label: p('export_total'), width: 15 },
        { key: 'paid_amount', label: p('export_paid'), width: 15 },
        { key: 'remaining_amount', label: p('export_remaining'), width: 15 },
        { key: 'payment_status', label: p('col_status'), width: 12 },
      ];
      
      const data = filteredData.map(item => ({
        purchase_number: item.purchase_number || '',
        supplier_name: item.supplier_name || '',
        invoice_number: item.invoice_number || '',
        purchase_date: item.purchase_date ? fmtDate(item.purchase_date) : '',
        total_amount: item.total_amount || 0,
        paid_amount: item.paid_amount || 0,
        remaining_amount: item.remaining_amount || 0,
        payment_status: item.payment_status || ''
      }));
      
      await generateReportExcel('purchases_ledger', p('report_title'), {
        sheetName: p('report_sheet'),
      });
      
    } catch (error) {
      console.error('Error generating Excel:', error);
      alert(p('alert_excel_error'));
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`/achats/livre-des-achats/${recordId}`, { payment_status: newStatus });
      await loadPurchases();
    } catch (error) {
      console.error('❌ Error updating status:', error);
    }
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find(s => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}><Icon size={14} />{label}</span>;
  };


  const getPaymentProgress = (paid, total) => {
    const paidAmount = parseFloat(paid) || 0;
    const totalAmount = parseFloat(total) || 0;
    
    if (totalAmount === 0) return 0;
    const percentage = ((paidAmount / totalAmount) * 100).toFixed(0);
    return percentage;
  };

  const getPaymentProgressBar = (paid, total) => {
    const percentage = parseFloat(getPaymentProgress(paid, total));
    
    let colorClass = 'bg-red-500';
    if (percentage === 100) colorClass = 'bg-green-500';
    else if (percentage >= 50) colorClass = 'bg-yellow-500';
    
    return (
      <div className="w-full bg-white/10 h-2 rounded-full overflow-hidden">
        <div className={`h-2 rounded-full ${colorClass} transition-all`} style={{ width: `${percentage}%` }}></div>
      </div>
    );
  };

  const getOverdueBadge = (dueDate, paymentStatus) => {
    if (paymentStatus === 'paid') return null;
    if (!dueDate) return null;
    
    const today = new Date();
    const due = new Date(dueDate);
    
    if (due < today) {
      const daysOverdue = Math.floor((today - due) / (1000 * 60 * 60 * 24));
      return <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400 border border-red-500/30 flex items-center gap-1">
        <AlertTriangle size={12} />
{p('overdue_days', { days: daysOverdue })}
      </span>;
    }
    
    return null;
  };

  const purchasesColumnKeys = useMemo(
    () => ['purchase_number', 'supplier_name', 'purchase_date', 'total_amount', 'payment_method', 'payment_status', 'actions'],
    [],
  );
  const { columnVisibility: purchasesColumnVisibility, handleToggleColumn: handleTogglePurchaseColumn, isColumnVisible: isPurchaseColumnVisible } = useAchatsColumnVisibility(purchasesColumnKeys);
  const purchasesColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'purchase_number', labelKey: 'col_purchase_number' },
      { key: 'supplier_name', labelKey: 'col_supplier' },
      { key: 'purchase_date', labelKey: 'col_date' },
      { key: 'total_amount', labelKey: 'col_amount' },
      { key: 'payment_method', labelKey: 'col_payment' },
      { key: 'payment_status', labelKey: 'col_status' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, purchasesColumnVisibility),
    [p, purchasesColumnVisibility],
  );
  const purchasesVisibleColumnCount = purchasesColumns.filter((c) => c.checked !== false).length;
  const purchasesTableColSpan = achatsTableColSpan(purchasesColumns);

  const ordersColumnKeys = useMemo(
    () => ['order_number', 'supplier_name', 'order_date', 'expected_delivery_date', 'total_amount', 'status', 'conversion', 'actions'],
    [],
  );
  const { columnVisibility: ordersColumnVisibility, handleToggleColumn: handleToggleOrderColumn, isColumnVisible: isOrderColumnVisible } = useAchatsColumnVisibility(ordersColumnKeys);
  const ordersColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'order_number', labelKey: 'col_order_number' },
      { key: 'supplier_name', labelKey: 'col_supplier' },
      { key: 'order_date', labelKey: 'col_order_date' },
      { key: 'expected_delivery_date', labelKey: 'col_delivery_date' },
      { key: 'total_amount', labelKey: 'col_amount' },
      { key: 'status', labelKey: 'col_status' },
      { key: 'conversion', labelKey: 'col_conversion' },
      { key: 'actions', labelKey: 'col_actions', required: true },
    ], p, ordersColumnVisibility),
    [p, ordersColumnVisibility],
  );
  const ordersVisibleColumnCount = ordersColumns.filter((c) => c.checked !== false).length;
  const ordersTableColSpan = ordersColumns.filter((c) => c.key !== 'actions' && c.checked !== false).length + 1;

  return (
    <div className={`space-y-6 employees-container`}>
      <div className="flex justify-between items-center list-toolbar-mobile-layout">
        <div>
          <h1 className="text-3xl font-bold text-white flex items-center gap-3">
            <BookOpen className="h-8 w-8 text-blue-400" />
            {p('title')}
            <button onClick={() => viewType === 'manual' ? loadPurchases() : loadOrders()} className="refresh-button"><RefreshCcw size={20} /></button>
          </h1>
          <p className="text-white/60 mt-2">{p('subtitle')}</p>
        </div>
        <div className="flex gap-3">
          <button onClick={() => setShowAdvancedOptions(!showAdvancedOptions)} className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400 shadow-lg shadow-blue-500/30' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold hover:border-white/30 transition-all duration-300 flex items-center space-x-2`}>
            <Filter className="h-5 w-5" /><span className="hidden sm:inline">{p('advanced_options')}</span>{showAdvancedOptions ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </button>
          {viewType === 'manual' && (
            <button className="bg-gradient-to-r from-blue-500 to-blue-700 text-white px-6 py-3 rounded-xl font-semibold hover:from-blue-600 hover:to-blue-800 transition-all duration-300 flex items-center space-x-2" onClick={() => { setEditingRecord(null); setFormTab('general');
    setShowModal(true); }}>
              <Plus className="h-5 w-5" /><span>{p('new_purchase')}</span>
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {(viewType === 'manual' ? [
          { title: p('stat_total_purchases'), value: stats.total, icon: BookOpen, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_pending'), value: stats.pending, icon: Clock, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_paid'), value: stats.paid, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_total_amount'), value: formatBrandMoney(stats.totalAmount), icon: DollarSign, color: 'from-purple-500 to-purple-600', isValue: true },
          { title: p('stat_remaining'), value: formatBrandMoney(stats.totalRemaining), icon: Receipt, color: 'from-red-500 to-red-600', isValue: true }
        ] : [
          { title: p('stat_total_orders'), value: orders.length, icon: Package, color: 'from-blue-500 to-blue-600' },
          { title: p('stat_pending'), value: orders.filter(o => o.status === 'pending').length, icon: Clock, color: 'from-orange-500 to-orange-600' },
          { title: p('stat_confirmed'), value: orders.filter(o => o.status === 'confirmed').length, icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { title: p('stat_in_transit'), value: orders.filter(o => o.status === 'in_transit').length, icon: Truck, color: 'from-purple-500 to-purple-600' },
          { title: p('stat_received'), value: orders.filter(o => o.status === 'received').length, icon: Receipt, color: 'from-emerald-500 to-emerald-600' }
        ]).slice(0, 4).map((stat, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }} className="relative group">
            <div className={`absolute -inset-0.5 bg-gradient-to-r ${stat.color} rounded-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 blur-sm`}></div>
            <div className="relative bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
              <div className="flex items-center justify-between">
                <div><p className="text-white/60 text-sm font-medium">{stat.title}</p><p className="text-2xl font-bold text-white force-white mt-2">{stat.isValue ? stat.value : stat.value}</p></div>
                <div className="relative">
                  <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} rounded-2xl opacity-0 group-hover:opacity-20 transition-opacity duration-300 blur-lg`}></div>
                  <div className={`relative p-3 rounded-2xl bg-gradient-to-r ${stat.color}`}><stat.icon className="h-6 w-6 text-white force-white" /></div>
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Toggle View Type */}
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1">
          <button
            onClick={() => setViewType('manual')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
              viewType === 'manual'
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <span>{p('tab_manual')}</span>
          </button>
          
          <button
            onClick={() => setViewType('converted')}
            className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${
              viewType === 'converted'
                ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            }`}
          >
            <span>{p('tab_from_orders')}</span>
          </button>
        </div>
      </div>

      {viewType === 'manual' && (
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-6 pt-6">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {mainTabs.map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
          {mainViewMode === 'all_purchases' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {quickFilterButtons.map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{distributionInfo.title}</h3>
              {activeDistributionFilter && (
                <button 
                  onClick={() => setActiveDistributionFilter(null)}
                  className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1"
                >
                  <X size={14} />
                  {p('reset_filter')}
                </button>
              )}
            </div>
            <div {...getErpOverviewGridAttrs(distributionInfo.data.length, '3', 'lg:grid-cols-4')}>{distributionInfo.data.map((item) => (
                <button
                  key={item.name}
                  onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.filterKey ? null : item.filterKey)}
                  className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${
                    activeDistributionFilter === item.filterKey 
                      ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' 
                      : 'border-white/10 hover:border-white/20 hover:bg-white/10'
                  }`}
                  title={p('filter_by_title', { name: item.name })}
                >
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.filterKey ? 'text-white' : 'text-white/80'}`}>{item.name}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                      return total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.filterKey ? 'text-white/80' : 'text-white/60'}`}>{(() => {
                    const total = distributionInfo.data.reduce((sum, d) => sum + d.count, 0);
                    const pct = total > 0 ? (item.count / total * 100).toFixed(1) : 0;
                    return p('pct_of_purchases', { pct });
                  })()}</p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>
      )}

      <div className="glass-panel glass-table-shell overflow-visible">
        <AchatsBulkDeleteBar
          selectedCount={selectedRecords.length}
          onDelete={handleBulkDelete}
          deleteLabel={`${t('common.delete')} (${selectedRecords.length})`}
        />
        <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  
                  {isPurchaseColumnVisible('purchase_number') ? <TableColumnHeaderFilter {...colHeader('purchase_number', p('col_purchase_number'))} /> : null}
                  {isPurchaseColumnVisible('supplier_name') ? <TableColumnHeaderFilter {...colHeader('supplier_name', p('col_supplier'))} /> : null}
                  {isPurchaseColumnVisible('purchase_date') ? <TableColumnHeaderFilter {...colHeader('purchase_date', p('col_date'))} /> : null}
                  {isPurchaseColumnVisible('total_amount') ? <TableColumnHeaderFilter {...colHeader('total_amount', p('col_amount'))} /> : null}
                  {isPurchaseColumnVisible('payment_method') ? <TableColumnHeaderFilter {...colHeader('payment_method', p('col_payment'), { kind: 'select', options: paymentMethodOptions })} /> : null}
                  {isPurchaseColumnVisible('payment_status') ? <TableColumnHeaderFilter {...colHeader('payment_status', p('col_status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={p('col_actions')}
                    columns={purchasesColumns}
                    onToggle={handleTogglePurchaseColumn}
                    visibleCount={purchasesVisibleColumnCount}
                    totalCount={purchasesColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {displayedPurchases.length > 0 ? (
                  displayedPurchases.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                      
                      {isPurchaseColumnVisible('purchase_number') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold flex items-center gap-2">
                            <BookOpen size={16} className="text-blue-400" />
                            {record.purchase_number}
                          </span>
                          {record.invoice_number && <span className="text-white/50 text-xs mt-0.5">{p('label_invoice')} {record.invoice_number}</span>}
                        </div>
                      </td>
                      ) : null}
                      
                      {isPurchaseColumnVisible('supplier_name') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{record.supplier_name || p('na')}</span>
                          {record.supplier_code && <span className="text-white/50 text-xs mt-0.5">{p('label_code')} {record.supplier_code}</span>}
                        </div>
                      </td>
                      ) : null}
                      
                      {isPurchaseColumnVisible('purchase_date') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <div className="flex items-center gap-1.5 text-white font-semibold">
                            <Clock size={14} className="text-white/50" />
                            {fmtDate(record.purchase_date)}
                          </div>
                          {record.due_date && (
                            <span className="text-white/50 text-xs mt-0.5">
                              {p('label_due')} {fmtDate(record.due_date)}
                            </span>
                          )}
                        </div>
                      </td>
                      ) : null}
                      
                      {isPurchaseColumnVisible('total_amount') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">{formatBrandMoney(parseFloat(record.total_amount || 0))}</span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {p('articles_count', { count: record.product_count || 0 })}
                          </span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isPurchaseColumnVisible('payment_method') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-green-400">{formatBrandMoney(parseFloat(record.paid_amount || 0))}</span>
                            <span className="text-white/50">{getPaymentProgress(record.paid_amount, record.total_amount)}%</span>
                          </div>
                          {getPaymentProgressBar(record.paid_amount, record.total_amount)}
                          <span className="text-red-400 text-xs">{p('label_remaining')} {formatBrandMoney(parseFloat(record.remaining_amount || 0))}</span>
                        </div>
                      </td>
                      ) : null}
                      
                      {isPurchaseColumnVisible('payment_status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex flex-col items-center gap-1">
                          <QawaemClickableStatusBadge status={record.payment_status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                          {getOverdueBadge(record.due_date, record.payment_status)}
                        </div>
                      </td>
                      ) : null}
                      
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={purchasesTableColSpan} className="text-center py-12 text-white/60"><BookOpen size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_purchases')}</p></td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewType === 'manual' && viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedPurchases.length > 0 ? (
              displayedPurchases.map((record, index) => (
                <motion.div
                  key={record.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2">
                          <BookOpen size={20} className="text-blue-400" />
                          {record.purchase_number}
                        </h3>
                        <div className="flex"><QawaemClickableStatusBadge status={record.payment_status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                        {getOverdueBadge(record.due_date, record.payment_status)}
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_supplier')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_name')}</span> {record.supplier_name || p('na')}</div>
                            <div><span className="font-medium">{p('label_code')}</span> {record.supplier_code || p('na')}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_dates')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            {record.purchase_date && <div><span className="font-medium">{p('label_purchase')}</span> {fmtDate(record.purchase_date)}</div>}
                            {record.invoice_date && <div><span className="font-medium">{p('label_invoice')}</span> {fmtDate(record.invoice_date)}</div>}
                            {record.due_date && <div><span className="font-medium">{p('label_due')}</span> {fmtDate(record.due_date)}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_amounts')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_total')}</span> {formatBrandMoney(parseFloat(record.total_amount || 0))}</div>
                            <div className="text-green-400"><span className="font-medium">{p('label_paid')}</span> {formatBrandMoney(parseFloat(record.paid_amount || 0))}</div>
                            <div className="text-red-400"><span className="font-medium">{p('label_remaining')}</span> {formatBrandMoney(parseFloat(record.remaining_amount || 0))}</div>
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_payment')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div>{getPaymentProgressBar(record.paid_amount, record.total_amount)}</div>
                            <div><span className="font-medium">{p('label_progress')}</span> {getPaymentProgress(record.paid_amount, record.total_amount)}%</div>
                            {record.payment_method && <div><span className="font-medium">{p('label_method')}</span> {getPaymentMethodLabel(record.payment_method)}</div>}
                          </div>
                        </div>
                        
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_details')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_articles')}</span> {record.product_count || 0}</div>
                            {record.warehouse_name && <div><span className="font-medium">{p('label_warehouse')}</span> {record.warehouse_name}</div>}
                            <p className="text-xs mt-2">{record.notes || p('no_notes')}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12">
                <BookOpen size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_purchases')}</p>
              </div>
            )}
          </div>
        )}

        {/* === DEPUIS COMMANDES: Orders Table === */}
        {viewType === 'converted' && (
          <div className="glass-table-scroll overflow-x-auto">
            {loadingOrders ? (
              <div className="text-center py-12">
                <RefreshCcw size={32} className="mx-auto mb-4 text-blue-400 animate-spin" />
                <p className={m.sectionChevron}>{p('loading_orders')}</p>
              </div>
            ) : filteredOrders.length > 0 ? (
              <table className="w-full employees-table data-table">
                <thead className="glass-table-head">
                  <tr>
                    {isOrderColumnVisible('order_number') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_order_number')}</th> : null}
                    {isOrderColumnVisible('supplier_name') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_supplier')}</th> : null}
                    {isOrderColumnVisible('order_date') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_order_date')}</th> : null}
                    {isOrderColumnVisible('expected_delivery_date') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_delivery_date')}</th> : null}
                    {isOrderColumnVisible('total_amount') ? <th className="px-6 py-4 text-left text-white font-semibold">{p('col_amount')}</th> : null}
                    {isOrderColumnVisible('status') ? <th className="px-6 py-4 text-center text-white font-semibold">{p('col_status')}</th> : null}
                    {isOrderColumnVisible('conversion') ? <th className="px-6 py-4 text-center text-white font-semibold">{p('col_conversion')}</th> : null}
                    <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                      label={p('col_actions')}
                      columns={ordersColumns}
                      onToggle={handleToggleOrderColumn}
                      visibleCount={ordersVisibleColumnCount}
                      totalCount={ordersColumns.length}
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10 employees-table-body">
                  {filteredOrders.map((order, index) => (
                    <tr 
                      key={order.id} 
                      className={`hover:bg-white/5 transition-colors ${order.is_converted ? 'bg-green-500/5' : ''}`}
                    >
                      {isOrderColumnVisible('order_number') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold flex items-center gap-2">
                            <Package size={16} className="text-blue-400" />
                            {order.order_number}
                          </span>
                          {order.payment_terms && <span className="text-white/50 text-xs mt-0.5">{order.payment_terms}</span>}
                        </div>
                      </td>
                      ) : null}

                      {isOrderColumnVisible('supplier_name') ? (
                      <td className="px-6 py-4">
                        <span className="text-white font-semibold">{order.supplier_name || p('na')}</span>
                      </td>
                      ) : null}

                      {isOrderColumnVisible('order_date') ? (
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1.5 text-white font-semibold">
                          <Clock size={14} className="text-white/50" />
                          {fmtDate(order.order_date)}
                        </div>
                        {order.order_date && (
                          <span className="text-white/40 text-xs">
                            {(() => {
                              const days = Math.floor((new Date() - new Date(order.order_date)) / (1000 * 60 * 60 * 24));
                              return days === 0 ? p('today') : days === 1 ? p('days_ago_one') : p('days_ago', { days });
                            })()}
                          </span>
                        )}
                      </td>
                      ) : null}

                      {isOrderColumnVisible('expected_delivery_date') ? (
                      <td className="px-6 py-4">
                        <span className="text-white/80">
                          {order.expected_delivery_date ? fmtDate(order.expected_delivery_date) : p('delivery_undefined')}
                        </span>
                      </td>
                      ) : null}

                      {isOrderColumnVisible('total_amount') ? (
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-white font-semibold">
                            {formatBrandMoney(parseFloat(order.total_amount || 0))}
                          </span>
                          <span className="text-white/50 text-xs mt-0.5">
                            {p('subtotal_vat', { subtotal: formatBrandMoney(parseFloat(order.subtotal || 0)), vat: formatBrandMoney(parseFloat(order.tax_amount || 0)) })}
                          </span>
                        </div>
                      </td>
                      ) : null}

                      {isOrderColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        {getOrderStatusBadge(order.status)}
                      </td>
                      ) : null}

                      {isOrderColumnVisible('conversion') ? (
                      <td className="px-6 py-4 text-center">
                        {order.is_converted ? (
                          <div className="flex flex-col items-center gap-1">
                            <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-green-500/20 text-green-300 border border-green-500/30">
                              <CheckCircle size={12} />
                              {p('converted')}
                            </span>
                            <span className="text-white/50 text-xs">{order.converted_purchase_number}</span>
                          </div>
                        ) : order.status === 'received' ? (
                          <button
                            onClick={() => handleConvertOrder(order.id)}
                            disabled={convertingOrderId === order.id}
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all shadow-lg shadow-blue-500/30 disabled:opacity-50"
                          >
                            {convertingOrderId === order.id ? (
                              <><RefreshCcw size={14} className="animate-spin" /> {p('converting')}</>
                            ) : (
                              <><Receipt size={14} /> {p('convert_to_purchase')}</>
                            )}
                          </button>
                        ) : (
                          <span className="text-white/40 text-xs italic">
                            {p('status_must_received', { status: getOrderStatusLabel(order.status) })}
                          </span>
                        )}
                      </td>
                      ) : null}

                      <td className="px-6 py-4">
                        <div className="flex items-center justify-center gap-1">
                          <button 
                            onClick={() => { setViewingRecord(order); setShowViewModal(true); }} 
                            className="p-1.5 text-blue-400 hover:text-blue-300 transition-colors" 
                            title={p('action_view')}
                          >
                            <Eye size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="text-center py-12">
                <Package size={48} className="mx-auto mb-4 text-white/30" />
                <p className="text-white/60 text-lg">{p('empty_orders')}</p>
                <p className="text-white/40 text-sm mt-2">{p('empty_orders_hint')}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* View Modal */}
      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="space-y-4">
                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>
                    <BookOpen size={18} className="text-blue-400" />
                    {p('section_identification')}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className={m.viewLabel}>{p('field_purchase_number')}</p>
                      <p className={m.viewValueLg}>{viewingRecord.purchase_number}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('field_invoice_number')}</p>
                      <p className={m.viewValue}>{viewingRecord.invoice_number || p('na')}</p>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('field_payment_status')}</p>
                      <div className="flex"><QawaemClickableStatusBadge status={viewingRecord.payment_status} recordId={viewingRecord.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div>
                    </div>
                    <div>
                      <p className={m.viewLabel}>{p('field_method')}</p>
                      <p className={m.viewValue}>{viewingRecord.payment_method ? getPaymentMethodLabel(viewingRecord.payment_method) : p('na')}</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={m.viewSection}>
                    <h3 className={m.viewSectionTitle}>
                      <Truck size={18} className="text-green-400" />
                      {p('section_supplier')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className={m.viewLabel}>{p('field_name')}</p>
                        <p className={m.viewValue}>{viewingRecord.supplier_name || p('na')}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('field_code')}</p>
                        <p className={m.viewValue}>{viewingRecord.supplier_code || p('na')}</p>
                      </div>
                    </div>
                  </div>

                  <div className={m.viewSection}>
                    <h3 className={m.viewSectionTitle}>
                      <DollarSign size={18} className="text-purple-400" />
                      {p('section_amounts')}
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <p className={m.viewLabel}>{p('field_total')}</p>
                        <p className={m.viewValueLg}>{formatBrandMoney(parseFloat(viewingRecord.total_amount || 0))}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('field_paid')}</p>
                        <p className="text-green-400 font-medium">{formatBrandMoney(parseFloat(viewingRecord.paid_amount || 0))}</p>
                      </div>
                      <div>
                        <p className={m.viewLabel}>{p('field_remaining')}</p>
                        <p className="text-red-400 font-medium">{formatBrandMoney(parseFloat(viewingRecord.remaining_amount || 0))}</p>
                      </div>
                      <div className="pt-2">
                        {getPaymentProgressBar(viewingRecord.paid_amount, viewingRecord.total_amount)}
                        <p className="text-white/60 text-xs mt-1">{p('label_progress')} {getPaymentProgress(viewingRecord.paid_amount, viewingRecord.total_amount)}%</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={m.viewSection}>
                  <h3 className={m.viewSectionTitle}>
                    <FileText size={18} className="text-yellow-400" />
                    {p('section_notes')}
                  </h3>
                  <p className="text-white/80">{viewingRecord.notes || p('no_notes')}</p>
                </div>
        </div>
      </QawaemErpViewModal>
      ) : null}

      {/* Edit/Create Modal */}
      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={editingRecord ? p('modal_edit') : p('modal_create')}
        icon={FileText}
        formId="purchase-form"
        saveLabel={t('common.save')}
        onSubmit
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <AchatsFormModalTabs
            tabs={formTabs}
            activeTab={formTab}
            onTabChange={setFormTab}
            isLight={m.isLight}
            ariaLabel={editingRecord ? p('modal_edit') : p('modal_create')}
          />
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="purchase-form" onSubmit={handleSubmit}>
                {formTab === 'general' && (
                  <div className={ACHATS_FORM_GRID}>
                    {editingRecord && (
                      <div className="md:col-span-3">
                        <label className={cfgTheme.label}>{p('field_purchase_number')}</label>
                        <input type="text" disabled value={editingRecord.purchase_number || ''} className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white/50 cursor-not-allowed" />
                      </div>
                    )}
                    <div>
                      <label className={cfgTheme.label}>{p('field_invoice_number_label')}</label>
                      <input type="text" value={formData.invoice_number} onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })} className={cfgTheme.input} placeholder={p('ph_invoice')} />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_purchase_date')}</label>
                      <CustomDatePicker value={formData.purchase_date} onChange={(date) => setFormData({ ...formData, purchase_date: date })} placeholder={p('ph_purchase_date')} />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_invoice_date')}</label>
                      <CustomDatePicker value={formData.invoice_date} onChange={(date) => setFormData({ ...formData, invoice_date: date })} placeholder={p('ph_invoice_date')} />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_due_date')}</label>
                      <CustomDatePicker value={formData.due_date} onChange={(date) => setFormData({ ...formData, due_date: date })} placeholder={p('ph_due_date')} minDate={formData.purchase_date} />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_supplier')}</label>
                      {loadingSuppliers ? (
                        <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading_orders')}</div>
                      ) : (
                        <CustomSelect
                          value={formData.supplier_id ? String(formData.supplier_id) : ''}
                          onChange={(value) => handleSupplierSelection(value)}
                          options={availableSuppliers.map((supplier) => ({
                            value: String(supplier.id),
                            label: `${supplier.company_name || supplier.name || p('col_supplier')}${supplier.contact_person ? ` — ${supplier.contact_person}` : ''}`,
                          }))}
                          placeholder={p('ph_select_supplier')}
                          searchable
                        />
                      )}
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_supplier_code')}</label>
                      <input type="text" value={formData.supplier_code} readOnly className="w-full bg-white/5 border border-white/20 rounded-xl px-4 py-2.5 text-white/70 cursor-not-allowed" placeholder={p('ph_supplier_code')} />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_warehouse')}</label>
                      {loadingWarehouses ? (
                        <div className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white/50">{p('loading_orders')}</div>
                      ) : (
                        <CustomSelect
                          value={formData.warehouse_name}
                          onChange={(value) => {
                            const selectedWarehouse = availableWarehouses.find(w => w.name === value);
                            setFormData({
                              ...formData,
                              warehouse_name: value,
                              warehouse_id: selectedWarehouse?.id || '',
                            });
                          }}
                          options={availableWarehouses.map(w => ({ value: w.name, label: w.name }))}
                          placeholder={p('ph_select_warehouse')}
                          searchable
                          creatable
                        />
                      )}
                    </div>
                  </div>
                )}

                {formTab === 'financial' && (
                  <div className={ACHATS_FORM_GRID}>
                    <div>
                      <label className={cfgTheme.label}>{p('field_total_amount')}</label>
                      <CustomNumberInput
                        value={formData.total_amount}
                        onChange={(value) => {
                          setFormData({
                            ...formData,
                            total_amount: value,
                            remaining_amount: (parseFloat(value) || 0) - (parseFloat(formData.paid_amount) || 0),
                          });
                        }}
                        placeholder={p('placeholder_decimal')}
                        step={0.01}
                        min={0}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_paid_amount')}</label>
                      <CustomNumberInput
                        value={formData.paid_amount}
                        onChange={(value) => {
                          setFormData({
                            ...formData,
                            paid_amount: value,
                            remaining_amount: (parseFloat(formData.total_amount) || 0) - (parseFloat(value) || 0),
                          });
                        }}
                        placeholder={p('placeholder_decimal')}
                        step={0.01}
                        min={0}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_remaining_amount')}</label>
                      <CustomNumberInput value={formData.remaining_amount} onChange={(value) => setFormData({ ...formData, remaining_amount: value })} placeholder={p('placeholder_decimal')} step={0.01} min={0} />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_tax')}</label>
                      <CustomNumberInput value={formData.tax_amount} onChange={(value) => setFormData({ ...formData, tax_amount: value })} placeholder={p('placeholder_decimal')} step={0.01} min={0} />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_discount')}</label>
                      <CustomNumberInput value={formData.discount_amount} onChange={(value) => setFormData({ ...formData, discount_amount: value })} placeholder={p('placeholder_decimal')} step={0.01} min={0} />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_product_count')}</label>
                      <CustomNumberInput value={formData.product_count} onChange={(value) => setFormData({ ...formData, product_count: value })} placeholder={p('placeholder_zero')} step={1} min={0} />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_payment_status_label')}</label>
                      <CustomSelect
                        value={formData.payment_status}
                        onChange={(value) => setFormData({ ...formData, payment_status: value })}
                        options={statusOptions.map(s => ({ value: s.value, label: s.label, icon: <s.icon size={16} /> }))}
                        placeholder={p('ph_status')}
                      />
                    </div>
                    <div>
                      <label className={cfgTheme.label}>{p('field_payment_method')}</label>
                      <CustomSelect
                        value={formData.payment_method}
                        onChange={(value) => setFormData({ ...formData, payment_method: value })}
                        options={paymentMethodOptions}
                        placeholder={p('ph_select')}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className={cfgTheme.label}>{p('section_notes')}</label>
                      <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={4} className={cfgTheme.input} placeholder={p('ph_notes')} />
                    </div>
                  </div>
                )}
            </form>
          </div>
        </div>
      </QawaemErpCrudModal>
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </div>
  );
};

export default LivreDesAchatsPage;

